import styled from '@emotion/styled';
import { useUser } from 'common/CacheProvider';
import { NextRouter, useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import useIsomorphicLayoutEffect from 'common/utils/useIsomorphicLayoutEffect';
import { SystemMessage } from 'common/Header/SystemMessage';
import lscache from 'lscache';

export const mainBg = 800;

const MainContainer = styled.main<{ system_message: SystemMessage | null, router: NextRouter }>(({ theme, system_message, router }) => {
  // Full-viewport player surfaces size themselves against the viewport, so the banner offset
  // would push their bottom edge past the fold instead of making room above them.
  const isPlayerSurface = router.asPath.includes('play') || router.asPath.includes('shorts');
  const offsetForBanner = !!system_message && !system_message?.isClosedBefore && !isPlayerSurface;

  return {
    position: 'relative',
    backgroundColor: theme.palette.background[mainBg][0],
    color: theme.palette.background[mainBg][1],
    overflowX: 'hidden',
    fontFamily: theme.font.family.default,
    lineHeight: theme.font.lineHeight.normal,
    ...theme.font.responsiveSize(1),
    direction: theme.direction,
    // A player surface is exactly one viewport tall and locks the page scroll itself. `100vh`
    // is measured against the large viewport, so holding that floor here would leave the page
    // a toolbar's worth of scroll on mobile — enough to drag the player out from under an
    // overlay sitting on top of it.
    minHeight: isPlayerSurface ? undefined : '100vh',
    display: 'flex',
    flexDirection: 'column',
    '& .mobile-only': {
      [theme.breakpoints.up('md')]: { display: 'none' },
    },
    '& .desktop-only': {
      [theme.breakpoints.down('md')]: { display: 'none' },
    },
    '& .en': {
      fontFamily: theme.font.family.english,
    },
    marginTop: offsetForBanner ? '72px' : 0,
    [theme.breakpoints.up('md')]: {
      marginTop: offsetForBanner ? '48px' : 0,
    },

  };
});

const Main = ({ children }) => {
  const [system_message, setSystemMessage] = useState<SystemMessage | null>(null);
  const { authorized, revalidate, loaded } = useUser();
  const router = useRouter();
  // Runs before first paint so marginTop is correct on the initial render — prevents CLS
  useIsomorphicLayoutEffect(() => {
    setSystemMessage(lscache.get('system_message'));
  }, []);

  useEffect(() => {
    window.addEventListener('storage', handleSystemMessage);

    const routes = ['/user/edit', '/user/password', '/user', '/packages', '/packages/successGift', '/packages/successPayment'];
    if (loaded && authorized && !routes.includes(router.pathname)) {
      revalidate();
    }
    return () => {
      window.removeEventListener('storage', handleSystemMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, loaded]);

  const handleSystemMessage = () => {
    setSystemMessage(lscache.get('system_message'));
  };

  return <MainContainer system_message={system_message} router={router}>{children}</MainContainer>;
};

export default Main;
